import { pricingPlans } from '@/mocks/corporateData';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '@/lib/formatPrice';

const planLocaleKeys = ['plan1', 'plan2', 'plan3', 'plan4', 'plan5'];
const optLocaleKeys = ['opt1', 'opt2'];
const optPrices = ['165,000〜', '110,000〜'];

export default function PricingTable() {
  const { t } = useTranslation();
  return (
    <section className="relative py-20 md:py-28 bg-background-100/50" id="pricing">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          {String(t('corporate.pricing.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('corporate.pricing.title')}
          </h2>
          {String(t('corporate.pricing.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto mb-5"></div>
          {String(t('corporate.pricing.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[16px] text-foreground-600 tracking-[0.04em] leading-relaxed max-w-[650px] mx-auto">{__s}</p>))}
        </div>

        {/* Main Pricing Table */}
        <div className="overflow-hidden mb-12">
          <div className="hidden md:grid grid-cols-5 bg-primary-900 text-foreground-50 text-[14px] tracking-[0.06em] font-label">
            <div className="p-4 text-center border-r border-white/10">{t('corporate.pricing.header.plan')}</div>
            <div className="p-4 text-center border-r border-white/10">{t('corporate.pricing.header.duration')}</div>
            <div className="p-4 text-center border-r border-white/10">{t('corporate.pricing.header.price')}</div>
            <div className="p-4 text-center border-r border-white/10">{t('corporate.pricing.header.note')}</div>
            <div className="p-4 text-center"></div>
          </div>

          {pricingPlans.map((plan, idx) => (
            <div
              key={idx}
              className={`grid grid-cols-1 md:grid-cols-5 border-b ${
                plan.recommended
                  ? 'bg-accent-500/5 border-l-2 border-r-2 border-accent-500/40'
                  : 'bg-background-50 border-background-200/60'
              }`}
            >
              {/* Mobile: combined row */}
              <div className="md:hidden p-4 flex flex-wrap items-center gap-2">
                <span className="text-[15px] text-foreground-900 font-medium tracking-[0.05em]">
                  {t(`corporate.pricing.${planLocaleKeys[idx]}.name`)}
                </span>
                {plan.recommended && (
                  <span className="text-[12px] bg-accent-500/80 text-primary-900 px-2 py-0.5 tracking-[0.08em] font-bold">
                    {t('corporate.pricing.recommended')}
                  </span>
                )}
                <span className="text-[14px] text-foreground-500 ml-auto">
                  {t(`corporate.pricing.${planLocaleKeys[idx]}.duration`)}
                </span>
              </div>
              <div className="md:hidden px-4 pb-3 flex items-center justify-between">
                <span className="text-[17px] text-accent-600 font-medium tracking-[0.04em]">
                  {formatPrice(plan.price)}{t('corporate.pricing.currency')}
                </span>
                <span className="text-[13px] text-foreground-400">
                  {t(`corporate.pricing.${planLocaleKeys[idx]}.note`)}
                </span>
              </div>

              {/* Desktop */}
              <div className="hidden md:flex items-center p-4 border-r border-background-200/60">
                <span className="text-[15px] text-foreground-900 font-medium tracking-[0.05em]">
                  {t(`corporate.pricing.${planLocaleKeys[idx]}.name`)}
                </span>
              </div>
              <div className="hidden md:flex items-center p-4 border-r border-background-200/60">
                <span className="text-[14px] text-foreground-600 tracking-[0.04em]">
                  {t(`corporate.pricing.${planLocaleKeys[idx]}.duration`)}
                </span>
              </div>
              <div className="hidden md:flex items-center p-4 border-r border-background-200/60">
                <span className="text-[17px] text-accent-600 font-medium tracking-[0.04em]">
                  {formatPrice(plan.price)}{t('corporate.pricing.currency')}
                </span>
              </div>
              <div className="hidden md:flex items-center p-4 border-r border-background-200/60">
                <span className="text-[14px] text-foreground-500 tracking-[0.03em]">
                  {t(`corporate.pricing.${planLocaleKeys[idx]}.note`)}
                </span>
              </div>
              <div className="hidden md:flex items-center justify-center p-4">
                {plan.recommended && (
                  <span className="text-[12px] bg-accent-500/80 text-primary-900 px-2.5 py-1 tracking-[0.08em] font-bold">
                    {t('corporate.pricing.recommended')}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Options */}
        <div className="mb-12">
          <h3 className="font-heading text-[16px] text-foreground-800 tracking-[0.06em] mb-4 text-center">
            {t('corporate.pricing.options.title')}
          </h3>
          <div className="max-w-[500px] mx-auto">
            {optLocaleKeys.map((key, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between p-4 bg-background-50 border border-background-200/60 mb-2"
              >
                <span className="text-[15px] text-foreground-700 tracking-[0.04em]">
                  {t(`corporate.pricing.${key}.name`)}
                </span>
                <div className="text-right">
                  <span className="text-[15px] text-accent-600 font-medium tracking-[0.04em]">
                    {t(`corporate.pricing.${key}.price`)}{t('corporate.pricing.options.currency')}
                  </span>
                  <p className="text-[13px] text-foreground-400 mt-0.5">
                    {t(`corporate.pricing.${key}.note`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Conditions */}
        <div className="max-w-[650px] mx-auto bg-background-50 border border-background-200/60 p-6 mb-10">
          <h3 className="font-heading text-[15px] text-foreground-800 tracking-[0.06em] mb-4">
            {t('corporate.pricing.conditions.title')}
          </h3>
          <ul className="space-y-2.5">
            <li className="flex items-start gap-2.5 text-[14px] text-foreground-500 tracking-[0.04em] leading-relaxed">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-accent-500 text-xs"></i>
              </div>
              {t('corporate.pricing.conditions.dispatch')}
            </li>
            <li className="flex items-start gap-2.5 text-[14px] text-foreground-500 tracking-[0.04em] leading-relaxed">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-accent-500 text-xs"></i>
              </div>
              {t('corporate.pricing.conditions.capacity')}
            </li>
            <li className="flex items-start gap-2.5 text-[14px] text-foreground-500 tracking-[0.04em] leading-relaxed">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-check-line text-accent-500 text-xs"></i>
              </div>
              {t('corporate.pricing.conditions.payment')}
            </li>
            <li className="flex items-start gap-2.5 text-[14px] text-foreground-400 tracking-[0.04em] leading-relaxed pt-2 border-t border-background-200/60">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-foreground-400 text-xs"></i>
              </div>
              {t('corporate.pricing.conditions.cancel')}
            </li>
          </ul>
        </div>

        {/* CTA after pricing */}
        <div className="text-center">
          <a
            href="#contact-form"
            className="btn-glow inline-block px-10 py-3.5 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('corporate.pricing.ctaBtn')}
          </a>
        </div>
      </div>
    </section>
  );
}
